<template>
  <div class="makercrate-cart-item">
    <div class="makercrate-cart-item__top">
      <!-- Title row -->
      <div class="makercrate-cart-item__title-row">
        <button
          type="button"
          class="makercrate-cart-item__edit-btn"
          aria-label="Edit kit name"
          @click="$emit('edit-name', cart.id)"
        >
          <img
            src="/images/icon/edit.svg"
            alt=""
            aria-hidden="true"
            width="24"
            height="24"
          />
        </button>

        <div v-if="!cart.isEditing" class="makercrate-cart-item__title">
          <span class="makercrate-cart-item__name">{{ cart.name }}&nbsp;•</span>
          <span class="makercrate-cart-item__count">
            {{ cart.activityCount }}/{{ cart.maxActivities }} Activities
          </span>
        </div>

        <div v-else class="makercrate-cart-item__edit-name">
          <input
            v-model="localName"
            type="text"
            class="makercrate-cart-item__name-input"
            @keyup.enter="$emit('save-name', cart.id, localName)"
            @keyup.esc="$emit('cancel-edit-name', cart.id)"
            :ref="(el) => el && el.focus()"
          />
          <ActionButton
            variant="primary"
            size="medium"
            @click="$emit('save-name', cart.id, localName)"
          >
            Save
          </ActionButton>
          <ActionButton
            variant="text"
            size="medium"
            @click="$emit('cancel-edit-name', cart.id)"
          >
            Cancel
          </ActionButton>
        </div>
      </div>

      <!-- Subtotal / qty / delete / duplicate -->
      <div class="makercrate-cart-item__actions-row">
        <div class="makercrate-cart-item__subtotal-qty">
          <p class="makercrate-cart-item__subtotal">
            <span class="makercrate-cart-item__subtotal-label">Subtotal:</span>
            ${{ formatPrice(cart.subtotal) }}
          </p>

          <div v-if="!quoteGenerated" class="makercrate-cart-item__qty">
            <button
              type="button"
              class="makercrate-cart-item__qty-btn"
              aria-label="Decrease quantity"
              :disabled="cart.quantity <= 1 || isUpdatingQuantity"
              @click="$emit('decrease-quantity', cart.id)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12H19"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <div class="makercrate-cart-item__qty-value" aria-label="Quantity">
              {{ cart.quantity || 1 }}
            </div>
            <button
              type="button"
              class="makercrate-cart-item__qty-btn"
              aria-label="Increase quantity"
              :disabled="isUpdatingQuantity"
              @click="$emit('increase-quantity', cart.id)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5V19M5 12H19"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div v-if="!quoteGenerated" class="makercrate-cart-item__buttons">
          <button
            type="button"
            class="makercrate-cart-item__btn makercrate-cart-item__btn--delete"
            @click="$emit('delete-cart', cart.id)"
          >
            <span>Delete</span>
            <img
              src="/images/delete.svg"
              alt=""
              aria-hidden="true"
              class="makercrate-cart-item__btn-icon makercrate-cart-item__btn-icon--delete"
            />
          </button>
        </div>
      </div>
    </div>

    <div class="makercrate-cart-item__divider" aria-hidden="true"></div>

    <div
      class="makercrate-cart-item__details"
      :class="{ collapsed: !cart.isExpanded }"
    >
      <div v-if="hasMetaBadges" class="makercrate-cart-item__badges">
        <span v-if="cart.studentsPerKit" class="makercrate-cart-item__badge">
          Students per kit:
          <strong>{{ cart.studentsPerKit }}</strong>
        </span>
        <span
          v-if="cart.instructionalHours"
          class="makercrate-cart-item__badge"
        >
          Instructional hours:
          <strong>{{ cart.instructionalHours }}</strong>
        </span>
      </div>

      <div class="makercrate-cart-item__content">
        <div class="makercrate-cart-item__image-wrap">
          <img
            :src="cart.image || '/images/maker-cart/makercrate-logo-v3.png'"
            :alt="cart.name"
            class="makercrate-cart-item__image"
          />
        </div>

        <div class="makercrate-cart-item__activities">
          <div
            v-for="(activity, index) in cart.activities"
            :key="`${activity.title}-${index}`"
            class="makercrate-cart-item__activity"
          >
            <p class="makercrate-cart-item__activity-title">
              {{ activity.title }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="makercrate-cart-item__toggle"
      :aria-expanded="cart.isExpanded"
      @click="$emit('toggle-view', cart.id)"
    >
      <span>{{ cart.isExpanded ? "View Less" : "View More" }}</span>
      <img
        src="/images/icon/double-chevron-up.svg"
        alt=""
        aria-hidden="true"
        class="makercrate-cart-item__toggle-icon"
        :class="{ rotated: !cart.isExpanded }"
      />
    </button>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, ref, watch } from "vue";
import ActionButton from "@/components/common/ActionButton.vue";
import { formatPrice } from "@/utils/index.js";

const props = defineProps({
  cart: {
    type: Object,
    required: true,
  },
  quoteGenerated: {
    type: Boolean,
    default: false,
  },
  isUpdatingQuantity: {
    type: Boolean,
    default: false,
  },
});

defineEmits([
  "edit-name",
  "save-name",
  "cancel-edit-name",
  "decrease-quantity",
  "increase-quantity",
  "delete-cart",
  "toggle-view",
]);

const localName = ref(props.cart.name);

watch(
  () => props.cart.name,
  (value) => {
    localName.value = value;
  }
);

const hasMetaBadges = computed(
  () => !!(props.cart.studentsPerKit || props.cart.instructionalHours)
);
</script>

<style scoped>
@import "@/assets/css/colors.css";

.makercrate-cart-item {
  width: 100%;
  background-color: #ededfc;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  box-shadow:
    0 1px 2px 0 rgba(0, 0, 0, 0.3),
    0 2px 6px 2px rgba(0, 0, 0, 0.15);
}

.makercrate-cart-item__top {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
}

.makercrate-cart-item__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.makercrate-cart-item__edit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

.makercrate-cart-item__edit-btn img {
  width: 24px;
  height: 24px;
  display: block;
}

.makercrate-cart-item__title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
  color: var(--black-body-text);
}

.makercrate-cart-item__name {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 700;
  line-height: 28px;
}

.makercrate-cart-item__count {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
}

.makercrate-cart-item__edit-name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  flex-wrap: wrap;
}

.makercrate-cart-item__name-input {
  height: 48px;
  min-width: 200px;
  flex: 1;
  padding: 0 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  color: var(--black-body-text);
  outline: none;
}

.makercrate-cart-item__actions-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.makercrate-cart-item__subtotal-qty {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  width: 438px;
  max-width: 100%;
  flex-shrink: 0;
}

.makercrate-cart-item__subtotal {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: var(--black-body-text);
  white-space: nowrap;
}

.makercrate-cart-item__subtotal-label {
  font-weight: 400;
}

.makercrate-cart-item__qty {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.makercrate-cart-item__qty-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: var(--primary-blue);
  cursor: pointer;
}

.makercrate-cart-item__qty-btn:hover:not(:disabled) {
  background-color: rgba(0, 61, 106, 0.05);
}

.makercrate-cart-item__qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.makercrate-cart-item__qty-value {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  background: var(--white);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--primary-blue);
}

.makercrate-cart-item__buttons {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.makercrate-cart-item__btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 56px;
  padding: 10px 16px;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.03px;
  white-space: nowrap;
}

.makercrate-cart-item__btn--delete {
  border: none;
  color: #40444b;
}

.makercrate-cart-item__btn--delete:hover {
  background-color: rgba(64, 68, 75, 0.06);
}

.makercrate-cart-item__btn-icon {
  width: 24px;
  height: 24px;
  display: block;
  flex-shrink: 0;
}

.makercrate-cart-item__btn-icon--delete {
  filter: brightness(0);
}

.makercrate-cart-item__divider {
  width: 100%;
  height: 0;
  border: none;
  border-top: 1px dashed #d0d5dd;
}

.makercrate-cart-item__details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
  overflow: hidden;
  max-height: 2000px;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease,
    gap 0.3s ease;
  opacity: 1;
}

.makercrate-cart-item__details.collapsed {
  max-height: 0;
  opacity: 0;
  gap: 0;
  pointer-events: none;
}

.makercrate-cart-item__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.makercrate-cart-item__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 16px;
  border-radius: 999px;
  background-color: var(--purple-light-2);
  color: var(--purple-dark-2);
  font-family: "League Spartan", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.048px;
  white-space: nowrap;
}

.makercrate-cart-item__badge strong {
  font-weight: 700;
  margin-left: 4px;
}

.makercrate-cart-item__content {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;
}

.makercrate-cart-item__image-wrap {
  width: 200px;
  height: 192px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.makercrate-cart-item__image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.makercrate-cart-item__activities {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  flex: 1;
  min-width: 0;
  align-content: start;
}

.makercrate-cart-item__activity {
  width: 100%;
  min-width: 0;
  padding: 12px;
  background: var(--white);
  border-radius: 24px;
  box-shadow:
    0 1px 2px 0 rgba(0, 0, 0, 0.3),
    0 2px 6px 2px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
}

.makercrate-cart-item__activity-title {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: var(--primary-blue);
  overflow-wrap: anywhere;
  word-break: break-word;
}

.makercrate-cart-item__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--primary-blue);
  align-self: center;
}

.makercrate-cart-item__toggle:hover {
  background-color: rgba(0, 61, 106, 0.05);
}

.makercrate-cart-item__toggle-icon {
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.makercrate-cart-item__toggle-icon.rotated {
  transform: rotate(180deg);
}

@media (max-width: 1100px) {
  .makercrate-cart-item__actions-row {
    flex-wrap: wrap;
  }

  .makercrate-cart-item__subtotal-qty {
    width: 100%;
  }

  .makercrate-cart-item__buttons {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .makercrate-cart-item {
    gap: 16px;
  }

  .makercrate-cart-item__top {
    gap: 16px;
  }

  .makercrate-cart-item__name,
  .makercrate-cart-item__count {
    font-size: var(--fs-medium);
    line-height: 24px;
  }

  .makercrate-cart-item__subtotal {
    font-size: var(--fs-large);
    line-height: 28px;
  }

  .makercrate-cart-item__qty-btn,
  .makercrate-cart-item__qty-value {
    width: 40px;
    height: 40px;
    padding: 8px;
  }

  .makercrate-cart-item__btn {
    height: 40px;
    padding: 8px 12px;
    font-size: var(--fs-medium);
    line-height: 24px;
  }

  .makercrate-cart-item__btn span {
    display: none;
  }

  .makercrate-cart-item__btn--delete {
    border: 1px solid #40444b;
  }

  .makercrate-cart-item__content {
    flex-direction: column;
    align-items: center;
  }

  .makercrate-cart-item__image-wrap {
    width: 160px;
    height: 154px;
  }

  .makercrate-cart-item__activities {
    width: 100%;
    grid-template-columns: 1fr;
  }

  .makercrate-cart-item__activity-title {
    font-size: var(--fs-large);
    line-height: 28px;
  }

  .makercrate-cart-item__badge {
    white-space: normal;
    height: auto;
    min-height: 24px;
    padding: 4px 12px;
  }
}
</style>
